import { bpOf, computeCodeSplit, computeLinkCommission, splitMultiYear } from "@mfa-aff/shared";
import type { CodeSplit, CodeSplitInput, MultiYearInput, MultiYearSplit } from "@mfa-aff/shared";
import { formatPercentBp } from "./digits";
import { formatToman, parseRial } from "./money";

/**
 * Display-side commission explanations (spec §7.3, §14.6.2). The formulas are the shared ones
 * (`@mfa-aff/shared` commission.ts, the same code the API runs); this file only turns them into
 * Persian calculation lines. The API stays the source of truth for stored amounts.
 */
export { computeCodeSplit, computeLinkCommission, splitMultiYear };
export type { CodeSplit, CodeSplitInput, MultiYearInput, MultiYearSplit };

/** C = floor(L × (r − d)). Equals `computeCodeSplit(...).affiliateGets` (and `computeLinkCommission` when d = 0). */
export function commissionRial(baseRial: bigint | string, rateBp: number, discountBp = 0): bigint {
    const L = parseRial(baseRial);
    if (discountBp === 0) return computeLinkCommission(L, rateBp);
    return bpOf(L, Math.max(0, rateBp - discountBp));
}

export type ExplainLine = {
    /** Operator shown before the line (first line has none). */
    op?: "×" | "−" | "+" | "=";
    label: string;
    value: string;
    /** Highlights the result line. */
    emphasis?: boolean;
};

export type CommissionExplanation = {
    lines: ExplainLine[];
    /** One-line sentence, e.g. «مبلغ مشمول 3٬400٬000 تومان × نرخ سطح نقره‌ای 22٪ − تخفیف شما 8٪ = 476٬000 تومان». */
    sentence: string;
    resultRial: bigint;
};

export function explainCommission(input: {
    baseRial: bigint | string;
    rateBp: number;
    discountBp?: number;
    levelName: string;
    /** e.g. «فروش جدید با کد» or «تمدید». */
    rateLabel?: string;
    /**
     * The stored amount, when known. Lower than the formula = the per-item cap
     * (`commission.max_item_rial`) applied: shown as its own line.
     */
    amountRial?: bigint | string;
}): CommissionExplanation {
    const { baseRial, rateBp, discountBp = 0, levelName, rateLabel } = input;
    const formulaRial = commissionRial(baseRial, rateBp, discountBp);
    const stored = input.amountRial !== undefined ? parseRial(input.amountRial) : null;
    const capped = stored !== null && stored < formulaRial;
    const resultRial = capped ? stored : formulaRial;
    const base = formatToman(baseRial);
    const rateName = rateLabel ? `نرخ ${rateLabel} سطح ${levelName}` : `نرخ سطح ${levelName}`;
    const lines: ExplainLine[] = [
        { label: "مبلغ مشمول (بدون مالیات و تخفیف فاکتور)", value: base },
        { op: "×", label: rateName, value: formatPercentBp(rateBp) },
    ];
    if (discountBp > 0) {
        lines.push({ op: "−", label: "تخفیفی که شما به مشتری دادید", value: formatPercentBp(discountBp) });
    }
    if (capped) {
        lines.push({ op: "=", label: "حاصل محاسبه", value: formatToman(formulaRial) });
        lines.push({ op: "=", label: "با سقف پورسانت هر قلم فروش", value: formatToman(resultRial), emphasis: true });
    } else {
        lines.push({ op: "=", label: "پورسانت شما", value: formatToman(resultRial), emphasis: true });
    }

    const sentence =
        `مبلغ مشمول ${formatToman(baseRial, { unit: false })} × ${rateName} ${formatPercentBp(rateBp)}` +
        (discountBp > 0 ? ` − تخفیف شما ${formatPercentBp(discountBp)}` : "") +
        ` = ${formatToman(formulaRial)}` +
        (capped ? `؛ با سقف پورسانت هر قلم فروش: ${formatToman(resultRial)}` : "");

    return { lines, sentence, resultRial };
}
